import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import { CreatorOptions, CreatorState } from '@/types/creator';
import { formatConfigValues } from '@/utils/utilities';
import { Dimension, UUID } from '@/types/types';
import { Attribute } from '@/api/AttributeApi';
import { Accessory } from '@/api/AccessoryApi';
import { ComponentAttributeValue } from '@/api/ComponentApi';
import {
	getCreatorSteps,
	getInitialChosenValues,
	displayAttributeValueName,
	getInitialAttributes,
	handleStateFiltering,
	getInitialAccessories,
	setLoadedChosenAccessories,
} from '@/utils/creatorUtils';
import ProductApi, { Product } from '@/api/ProductApi';

const initialState: CreatorState = {
	clientId: null,
	product: null,
	quantity: 1,
	availableAttributes: {},
	availableAccessories: [],
	chosenProductAttributes: {},
	chosenAccessories: {},
	dimensionConfigValues: {},
	options: {
		availableSteps: [],
		currentStep: 0,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		initialize: (
			state,
			{
				payload,
			}: PayloadAction<Awaited<ReturnType<typeof ProductApi.getWithItem>>>
		) => {
			const { product, productType, entityItem } = payload;

			state.product = product;
			state.product.productType = productType;
			state.options.availableSteps = getCreatorSteps(product.productType);

			state.dimensionConfigValues = formatConfigValues(
				product.productType.dimensionsConfig,
				product.dimensions || []
			);
			state.availableAttributes = getInitialAttributes(
				state.product.productType.components
			);
			state.availableAccessories = getInitialAccessories(
				state.product.productType.accessories
			);
			state.chosenProductAttributes = getInitialChosenValues(
				state.product.productType.components,
				product.productAttributes
			);
			Object.assign(
				state.chosenAccessories,
				setLoadedChosenAccessories(product.accessories)
			);

			handleStateFiltering(state);

			if (entityItem) {
				state.quantity = entityItem.quantity;
			}
		},
		selectProductAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentId: UUID;
				attribute: Attribute;
				componentAttributeValue: ComponentAttributeValue;
			}>
		) => {
			if (
				state.chosenProductAttributes[payload.componentId][payload.attribute.id]
					.componentAttributeValue?.id === payload.componentAttributeValue.id
			) {
				state.chosenProductAttributes[payload.componentId][
					payload.attribute.id
				] = {
					...state.chosenProductAttributes[payload.componentId][
						payload.attribute.id
					],
					componentAttributeValue: null,
					value: '',
				};
				handleStateFiltering(state);
				return;
			}

			state.chosenProductAttributes[payload.componentId][payload.attribute.id] =
				{
					componentAttributeValue: payload.componentAttributeValue,
					attribute: payload.attribute,
					value: displayAttributeValueName(
						payload.componentAttributeValue.attributeValue,
						payload.attribute
					),
				};

			handleStateFiltering(state);
		},
		filterAttributes: (state) => {
			handleStateFiltering(state);
		},
		setProductAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				componentId: UUID;
				attribute: Attribute;
				value: string;
			}>
		) => {
			state.chosenProductAttributes[payload.componentId][payload.attribute.id] =
				{
					componentAttributeValue: null,
					attribute: payload.attribute,
					value: payload.value,
				};
		},
		addAccessory: (state, { payload }: PayloadAction<Accessory>) => {
			if (state.chosenAccessories[payload.category.id]?.id === payload.id) {
				delete state.chosenAccessories[payload.category.id];
				return;
			}

			state.chosenAccessories[payload.category.id] = payload;
		},
		updateProduct: (state, { payload }: PayloadAction<Partial<Product>>) => {
			if (!state.product) return;

			Object.assign(state.product, payload);
		},
		updateProductWithSync: (
			state,
			{ payload }: PayloadAction<Partial<Product>>
		) => {
			if (!state.product) return;

			Object.assign(state.product, payload);
		},
		setDimensions: (
			state,
			{ payload }: PayloadAction<CreatorState['dimensionConfigValues']>
		) => {
			if (!state.product) return;

			state.dimensionConfigValues = payload;
		},
		updateDimensions: (
			state,
			{
				payload,
			}: PayloadAction<{
				shortName: string;
				dimension: Dimension;
				value: number;
			}>
		) => {
			if (!state.product) return;

			state.dimensionConfigValues[payload.shortName][payload.dimension] =
				payload.value;
		},
		navigateStep: (state, { payload }: PayloadAction<'next' | 'prev'>) => {
			if (payload === 'next')
				do {
					state.options.currentStep++;
				} while (
					state.options.availableSteps[state.options.currentStep].disabled
				);
			else
				do {
					state.options.currentStep--;
				} while (
					state.options.availableSteps[state.options.currentStep].disabled
				);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		resetCreator: (state) => {
			Object.assign(state, initialState);
		},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;

export const getDimensionsForSummary = createSelector(
	[selectCreatorState],
	(state) => {
		const result = [];
		for (const [shortName, dimensions] of Object.entries(
			state.dimensionConfigValues
		)) {
			const name =
				state.product?.productType.dimensionsConfig.find(
					(config) => config.shortName === shortName
				)?.name || 'Nie odnaleziono';

			result.push({
				name,
				...dimensions,
			});
		}

		return result;
	}
);
export default creatorSlice.reducer;
